import './TimelineCopyModal.scss';
import React, { useState } from 'react';
import { useStateSelector } from '../../../../store/selectors';
import Rodal from '../../../../components/Rodal/Rodal';
import * as yup from 'yup';
import { Form, Field } from '@availity/form';
import createNotification from '../../../../utils/createNotification';
import Loader from 'react-loaders';
import { ModalBody, ModalHeader } from 'reactstrap';
import { Timeline } from '../../../../slices/marketplace/timeline/models/timeline.model';

export interface TimelineCopyModalProps {
    template: Timeline;
    onClose: (id: number | undefined) => void;
    fetchData: () => void;
    isVisible: boolean;
}

interface CopyTemplateFormValues {
    newName: string;
}

const TimelineCopyModal = (props: TimelineCopyModalProps) => {
    const axios = useStateSelector((state) => state.core.axios);

    const [isLoading, setIsLoading] = useState(false);

    const [existingNames, setExistingNames] = useState([props.template.name]);

    const makeTimeineCopy = (newName: string) => {
        axios
            .post(
                `api/Marketplace/TimelineTemplates/${props.template.id}/copy`,
                {
                    name: newName,
                }
            )
            .then((response: any) => {
                if (response) {
                    if (response.status === 200) {
                        createNotification(
                            'Timeline Template copied successfully',
                            'success'
                        );
                        props.onClose(response.data);
                        props.fetchData();
                    } else if (
                        response.response &&
                        response.response.status === 409
                    ) {
                        setExistingNames([...existingNames, newName]);
                        createNotification(
                            'Timeline Template with such name already exists.',
                            'error'
                        );
                    } else {
                        createNotification(
                            'An unexpected error occurred while copying Timeline Template.',
                            'error'
                        );
                    }
                }
            })
            .catch(() => {
                createNotification(
                    'An unexpected error occurred while copying Timeline Template.',
                    'error'
                );
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const renderForm = () => (
        <Form
            initialValues={{
                newName: `${props.template.name} - Copy`,
            }}
            validationSchema={yup.object().shape({
                newName: yup
                    .string()
                    .required('Template copy name is required')
                    .notOneOf(existingNames, 'This name is already used'),
            })}
            onSubmit={(values: CopyTemplateFormValues) =>
                makeTimeineCopy(values.newName)
            }>
            <div className="d-flex flex-column">
                <div className="form-group row">
                    <div className="col-md-3 d-flex mt-2">
                        <label className="copy-name-label" htmlFor="newName">
                            Copy Name:
                        </label>
                    </div>
                    <div className="col-md-9">
                        <Field
                            type="text"
                            name="newName"
                            id="newName"
                            placeholder="Enter group name"
                            className="form-control"
                        />
                    </div>
                </div>
                <div className="mt-auto d-flex justify-content-end modal-copy-buttons">
                    <button
                        type="button"
                        className="btn btn-cancel"
                        onClick={() => props.onClose(undefined)}>
                        Cancel
                    </button>
                    <button
                        type="submit"
                        className="btn btn-primary"
                        disabled={isLoading}>
                        Copy Template
                    </button>
                </div>
            </div>
        </Form>
    );

    const renderLoader = () => {
        return (
            <div className="loader-container" style={{ height: '10vw' }}>
                <div className="loader-container-inner">
                    <div className="text-center">
                        <Loader active={isLoading} type="ball-scale-multiple" />
                    </div>
                    <h6 className="mt-5">Adding new corporate group...</h6>
                </div>
            </div>
        );
    };

    return (
        <div>
            <Rodal
                visible={props.isVisible}
                animation={'fade'}
                onClose={() => props.onClose(undefined)}
                showMask={true}
                width={600}
                closeOnEsc
                className="modal-copy-timeline-popup"
                center>
                <ModalHeader>Timeline Template Copy</ModalHeader>
                <ModalBody>
                    {isLoading ? renderLoader() : renderForm()}
                </ModalBody>
            </Rodal>
        </div>
    );
};

export default TimelineCopyModal;
